import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, MessageCircle, Send, MapPin, Map } from 'lucide-react'
import { BottomNavigator } from '@/components/BottomNavigator'
import { spacing } from '@/theme'

type SupportCity = {
  id: string
  name: string
  mapQuery: string
  whatsappUrl: string
  telegramUrl: string
}

const supportCities: SupportCity[] = [
  {
    id: 'bogota',
    name: 'Bogota, Cundinamarca',
    mapQuery: 'Bogota, Colombia',
    whatsappUrl: 'https://chat.whatsapp.com/invite/bogota-wingdrive',
    telegramUrl: 'https://t.me/wingdrive_bogota',
  },
  {
    id: 'medellin',
    name: 'Medellin, Antioquia',
    mapQuery: 'Medellin, Colombia',
    whatsappUrl: 'https://chat.whatsapp.com/invite/medellin-wingdrive',
    telegramUrl: 'https://t.me/wingdrive_medellin',
  },
  {
    id: 'cali',
    name: 'Cali, Valle del Cauca',
    mapQuery: 'Cali, Colombia',
    whatsappUrl: 'https://chat.whatsapp.com/invite/cali-wingdrive',
    telegramUrl: 'https://t.me/wingdrive_cali',
  },
  {
    id: 'barranquilla',
    name: 'Barranquilla, Atlantico',
    mapQuery: 'Barranquilla, Colombia',
    whatsappUrl: 'https://chat.whatsapp.com/invite/barranquilla-wingdrive',
    telegramUrl: 'https://t.me/wingdrive_barranquilla',
  },
  {
    id: 'bucaramanga',
    name: 'Bucaramanga, Santander',
    mapQuery: 'Bucaramanga, Colombia',
    whatsappUrl: 'https://chat.whatsapp.com/invite/bucaramanga-wingdrive',
    telegramUrl: 'https://t.me/wingdrive_bucaramanga',
  },
]

export function RedApoyoScreen() {
  const navigate = useNavigate()
  const [selectedCityId, setSelectedCityId] = useState(supportCities[0].id)

  const selectedCity = useMemo(
    () => supportCities.find((city) => city.id === selectedCityId) || supportCities[0],
    [selectedCityId]
  )

  const googleMapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(selectedCity.mapQuery)}&z=11&output=embed`

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          padding: spacing[4],
          paddingBottom: '56px',
          background: 'linear-gradient(165deg, #022c22 0%, #064e3b 45%, #0f172a 100%)',
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <button
            onClick={() => navigate('/interacciones')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing[2],
              color: '#a7f3d0',
              marginBottom: spacing[3],
            }}
          >
            <ArrowLeft size={18} />
            Volver
          </button>

          <div
            style={{
              borderRadius: 18,
              padding: spacing[4],
              marginBottom: spacing[4],
              border: '1px solid rgba(16, 185, 129, 0.45)',
              background: 'linear-gradient(135deg, rgba(6, 95, 70, 0.8) 0%, rgba(15, 23, 42, 0.45) 100%)',
              color: '#d1fae5',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[1] }}>
              <Map size={22} />
              <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>Red de Apoyo - Colombia</h1>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#a7f3d0' }}>
              Selecciona una ciudad y abre su grupo de apoyo en WhatsApp o Telegram.
            </p>
          </div>

          <div
            style={{
              borderRadius: 16,
              overflow: 'hidden',
              border: '1px solid rgba(110, 231, 183, 0.4)',
              backgroundColor: 'rgba(6, 78, 59, 0.55)',
              marginBottom: spacing[3],
            }}
          >
            <iframe
              title={`Mapa de ${selectedCity.name}`}
              src={googleMapSrc}
              width="100%"
              height="260"
              loading="lazy"
              style={{ border: 0, display: 'block' }}
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <div style={{ display: 'flex', gap: spacing[2], flexWrap: 'wrap', marginBottom: spacing[3] }}>
            {supportCities.map((city) => (
              <button
                key={city.id}
                onClick={() => setSelectedCityId(city.id)}
                style={{
                  padding: `${spacing[2]} ${spacing[3]}`,
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  color: selectedCityId === city.id ? '#052e16' : '#d1fae5',
                  backgroundColor: selectedCityId === city.id ? '#34d399' : 'rgba(15, 23, 42, 0.65)',
                  border: selectedCityId === city.id ? '1px solid #a7f3d0' : '1px solid rgba(110, 231, 183, 0.35)',
                }}
              >
                {city.name}
              </button>
            ))}
          </div>

          <motion.div
            key={selectedCity.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              borderRadius: 16,
              border: '1px solid rgba(110, 231, 183, 0.4)',
              backgroundColor: 'rgba(6, 78, 59, 0.8)',
              padding: spacing[4],
              color: '#d1fae5',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[1] }}>
              <MapPin size={18} color="#6ee7b7" />
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{selectedCity.name}</h3>
            </div>
            <p style={{ margin: `0 0 ${spacing[3]}`, color: '#a7f3d0', fontSize: 13 }}>
              Elige donde quieres participar en el grupo de apoyo local.
            </p>

            <div style={{ display: 'flex', gap: spacing[2], flexWrap: 'wrap' }}>
              <button
                onClick={() => window.open(selectedCity.whatsappUrl, '_blank', 'noopener,noreferrer')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: spacing[2],
                  padding: `${spacing[2]} ${spacing[3]}`,
                  borderRadius: 10,
                  backgroundColor: '#22c55e',
                  color: '#052e16',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                <MessageCircle size={16} />
                Abrir WhatsApp
              </button>
              <button
                onClick={() => window.open(selectedCity.telegramUrl, '_blank', 'noopener,noreferrer')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: spacing[2],
                  padding: `${spacing[2]} ${spacing[3]}`,
                  borderRadius: 10,
                  backgroundColor: '#38bdf8',
                  color: '#082f49',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                <Send size={16} />
                Abrir Telegram
              </button>
            </div>
          </motion.div>
        </div>
      </div>
      <BottomNavigator />
    </>
  )
}

